import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CountryDetail,
  type CountryListItem,
  CreateCountryRequestSchema,
  type CreateCountryRequest,
  UpdateCountryRequestSchema,
  type UpdateCountryRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateCountry, useUpdateCountry } from '../use-countries';

interface CountryFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  country?: CountryDetail | CountryListItem | null;
}

const CREATE_DEFAULTS: CreateCountryRequest = {
  isoCode: '',
  isoCode3: '',
  name: '',
  phoneCode: '',
  flagEmoji: '',
  sortOrder: 0,
  isActive: true,
};

const upper = (s: string): string => s.toUpperCase();

const CountryFields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. India"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isoCode">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          onChange={(e) => field.onChange(upper(e.target.value))}
          label="ISO code (2 letters)"
          placeholder="IN"
          maxLength={2}
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isoCode3">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          onChange={(e) => field.onChange(upper(e.target.value))}
          label="ISO code (3 letters)"
          placeholder="IND"
          maxLength={3}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="phoneCode">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Phone code"
          placeholder="+91"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="flagEmoji">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Flag emoji"
          placeholder="🇮🇳"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const CountryFormDrawer = ({
  open,
  onOpenChange,
  mode,
  country,
}: CountryFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createCountry = useCreateCountry();
  const updateCountry = useUpdateCountry();
  const pending = createCountry.isPending || updateCountry.isPending;

  const createForm = useForm<CreateCountryRequest>({
    resolver: zodResolver(CreateCountryRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateCountryRequest>({
    resolver: zodResolver(UpdateCountryRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (country) {
      updateForm.reset({
        isoCode: country.isoCode,
        isoCode3: country.isoCode3 ?? '',
        name: country.name,
        phoneCode: country.phoneCode ?? '',
        flagEmoji: country.flagEmoji ?? '',
        sortOrder: country.sortOrder,
        isActive: country.isActive,
      });
    }
  }, [open, isCreate, country, createForm, updateForm]);

  const normalize = <
    T extends { isoCode3?: string | null; phoneCode?: string | null; flagEmoji?: string | null },
  >(
    v: T,
  ): T => ({
    ...v,
    isoCode3: v.isoCode3?.trim() ? v.isoCode3 : null,
    phoneCode: v.phoneCode?.trim() ? v.phoneCode : null,
    flagEmoji: v.flagEmoji?.trim() ? v.flagEmoji : null,
  });

  const onCreate = createForm.handleSubmit(async (values) => {
    await createCountry.mutateAsync(normalize(values));
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!country) return;
    await updateCountry.mutateAsync({ id: country.id, payload: normalize(values) });
    onOpenChange(false);
  });

  const fields = <CountryFields pending={pending} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add country' : `Edit ${country?.name ?? 'country'}`}
      description="ISO codes are normalized to uppercase. Inactive countries are hidden from pickers."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
